import { useEffect, useState } from 'react';
import {
  BuildingStorefrontIcon,
  UsersIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { reportsApi, visitsApi, casesApi, CompanyReport, Visit, Case } from '../services/api';

export default function Dashboard() {
  const [report, setReport] = useState<CompanyReport | null>(null);
  const [recentVisits, setRecentVisits] = useState<Visit[]>([]);
  const [recentCases, setRecentCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [reportData, visitsData, casesData] = await Promise.all([
          reportsApi.getCompanyReport(),
          visitsApi.list({ per_page: 5 }).catch(() => ({ data: [], meta: { page: 1, per_page: 5, total: 0 } })),
          casesApi.list().catch(() => []),
        ]);
        setReport(reportData);
        setRecentVisits(visitsData.data);
        setRecentCases(casesData.filter(c => c.status === 'open').slice(0, 5));
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  const stats = [
    {
      name: 'Total Stores',
      value: report?.total_stores ?? '-',
      icon: BuildingStorefrontIcon,
      color: 'bg-blue-500'
    },
    {
      name: 'Visits This Month',
      value: report?.total_visits_this_month ?? '-',
      icon: ClipboardDocumentCheckIcon,
      color: 'bg-purple-500'
    },
    {
      name: 'Open Cases',
      value: report?.open_cases?.total ?? '-',
      icon: ExclamationTriangleIcon,
      color: 'bg-orange-500'
    },
    {
      name: 'Avg Score',
      value: report ? `${Math.round(report.average_score)}%` : '-',
      icon: UsersIcon,
      color: 'bg-green-500'
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of store visit activity
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-white overflow-hidden shadow-sm rounded-xl"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${stat.color} rounded-lg p-3`}>
                  <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.name}
                    </dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Average Score Card */}
      {report && (
        <div className="mt-8 bg-white shadow-sm rounded-xl p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Company Average Score</h3>
          <div className="flex items-center">
            <div className={`text-4xl font-bold ${
              report.average_score >= 90 ? 'text-green-600' :
              report.average_score >= 70 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {Math.round(report.average_score)}%
            </div>
            <div className="ml-4 flex-1">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className={`h-4 rounded-full ${
                    report.average_score >= 90 ? 'bg-green-500' :
                    report.average_score >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${report.average_score}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity Grid */}
      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Recent Visits */}
        <div className="bg-white shadow-sm rounded-xl p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Recent Visits
          </h3>
          {recentVisits.length > 0 ? (
            <div className="space-y-3">
              {recentVisits.map((visit) => (
                <div key={visit.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{visit.store_name || 'Store'}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(visit.started_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className={`px-2.5 py-0.5 rounded-full text-sm font-medium ${
                    (visit.overall_score ?? 0) >= 90 ? 'bg-green-100 text-green-800' :
                    (visit.overall_score ?? 0) >= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {visit.overall_score ?? 0}%
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No recent visits</p>
          )}
        </div>

        {/* Open Cases */}
        <div className="bg-white shadow-sm rounded-xl p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Open Cases
          </h3>
          {recentCases.length > 0 ? (
            <div className="space-y-3">
              {recentCases.map((caseItem) => (
                <div key={caseItem.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{caseItem.store_name || 'Store'}</p>
                    <p className="text-sm text-gray-500 truncate max-w-xs">
                      {caseItem.question_text || 'Issue'}
                    </p>
                  </div>
                  <div className={`px-2.5 py-0.5 rounded-full text-sm font-medium ${
                    caseItem.severity === 'critical' ? 'bg-red-100 text-red-800' :
                    caseItem.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                    caseItem.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {caseItem.severity}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No open cases</p>
          )}
        </div>
      </div>
    </div>
  );
}
